import React, { useState, useEffect, useRef } from 'react';
import { slideApiService } from '../services/slideApiService';

const SW_TAG = '[CSAI-SW]';

interface AuthenticatedImageProps {
  shopId: string;
  slideId: string;
  imageId: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  size?: 'thumb_300' | 'thumb_800';
  /** Called when a HEIC image is detected (pending async conversion).
   *  Parent should periodically re-fetch slide data so imageId prop updates
   *  when the conversion completes and the image ID is swapped. */
  onHeicPending?: () => void;
}

/**
 * Displays an authenticated slide image.
 *
 * - **With Service Worker**: renders <img src> — the SW injects the Bearer
 *   token, browser HTTP cache handles caching.
 * - **Without Service Worker**: falls back to fetch() with auth headers and
 *   blob URLs.
 *
 * Image IDs are immutable (content-addressed). When image content changes
 * (rotation/crop/HEIC conversion), a new image ID is generated, so the URL
 * itself changes and no cache-busting is needed.
 *
 * HEIC handling: When a HEIC file is detected, the component shows a
 * "Processing image" spinner and calls onHeicPending(). The parent is
 * responsible for re-fetching slide data — when the Cloud Function converts
 * the HEIC and swaps the image ID, the parent will get the new imageId and
 * pass it as a prop, causing this component to re-render with the PNG.
 */
const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  shopId,
  slideId,
  imageId,
  alt,
  className,
  style,
  loading = 'lazy',
  size,
  onHeicPending,
}) => {
  // Decide path once on mount and stick with it. If the SW is already
  // controlling the page, use SwImage. Otherwise use FetchImage and let it
  // finish — don't switch mid-render if the SW activates later.
  const [hasSW] = useState(() => Boolean(navigator.serviceWorker?.controller));

  if (hasSW) {
    return (
      <SwImage
        shopId={shopId} slideId={slideId} imageId={imageId}
        alt={alt} className={className} style={style} loading={loading}
        size={size} onHeicPending={onHeicPending}
      />
    );
  }

  return (
    <FetchImage
      shopId={shopId} slideId={slideId} imageId={imageId}
      alt={alt} className={className} style={style} loading={loading}
      size={size} onHeicPending={onHeicPending}
    />
  );
};

/** Service Worker path — plain <img src>, SW injects token, browser caches. */
const SwImage: React.FC<AuthenticatedImageProps> = ({
  shopId, slideId, imageId, alt, className, style, loading, size, onHeicPending,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHeicPending, setIsHeicPending] = useState(false);
  const onHeicPendingRef = useRef(onHeicPending);
  onHeicPendingRef.current = onHeicPending;
  const mountTimeRef = useRef(performance.now());
  const imgRef = useRef<HTMLImageElement>(null);

  const imageUrl = slideApiService.getImageUrl(shopId, slideId, imageId, size);

  // Reset state when imageId changes (e.g., after HEIC swap)
  useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
    setIsHeicPending(false);
    mountTimeRef.current = performance.now();
  }, [imageId]);

  // If the image is already complete (from browser cache), onLoad may not fire
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0 && !isLoaded) {
      setIsLoaded(true);
    }
  });

  // Watchdog: if a visible/eager image hasn't loaded after 10s, log diagnostic info.
  // Skip for lazy/hidden images — the browser intentionally defers those.
  useEffect(() => {
    if (loading !== 'eager') return;
    const timer = setTimeout(() => {
      if (!isLoaded && !hasError && !isHeicPending) {
        const img = imgRef.current;
        console.error(`${SW_TAG} Image not loaded after 10s — imageId=${imageId}, url=${imageUrl}`);
        if (img) {
          console.error(`${SW_TAG}   complete=${img.complete}, naturalWidth=${img.naturalWidth}, currentSrc=${img.currentSrc || 'empty'}`);
          const rect = img.getBoundingClientRect();
          console.error(`${SW_TAG}   boundingRect: ${rect.width.toFixed(0)}x${rect.height.toFixed(0)} at (${rect.left.toFixed(0)},${rect.top.toFixed(0)})`);
        }
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [isLoaded, hasError, isHeicPending, imageId, imageUrl, loading]);

  const handleError = async () => {
    // Image failed to load — check if it's a HEIC file (browsers can't render HEIC natively)
    try {
      const headResponse = await slideApiService.fetchImageHeaders(shopId, slideId, imageId, size);
      const contentType = headResponse.headers.get('Content-Type') || '';
      const isHeic = contentType.includes('image/heic') || contentType.includes('image/heif');
      if (isHeic) {
        setIsHeicPending(true);
        onHeicPendingRef.current?.();
        return;
      }
    } catch {
      // HEAD request failed — fall through to error state
    }
    const elapsed = (performance.now() - mountTimeRef.current).toFixed(0);
    console.warn(`${SW_TAG} Image load failed after ${elapsed}ms — imageId=${imageId}`);
    setHasError(true);
  };

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHasError(false);
    setIsLoaded(false);
    mountTimeRef.current = performance.now();
  };

  if (isHeicPending) {
    return (
      <div className={className} style={{ position: 'relative', ...style, overflow: 'hidden' }}>
        <HeicProcessingSpinner />
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 ${className || ''}`}
        style={{ ...style, cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={handleRetry}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px', color: 'var(--text-secondary)' }}>
            &#8635;
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Tap to retry</div>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ position: 'relative', ...style, overflow: 'hidden' }}>
      {!isLoaded && (
        <div
          className="flex items-center justify-center bg-gray-100 text-gray-500"
          style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        >
          <div className="text-center">
            <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mb-1"></div>
            <div className="text-xs">Loading...</div>
          </div>
        </div>
      )}
      <img
        ref={imgRef}
        src={imageUrl}
        alt={alt}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: style?.objectFit || 'cover',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
        loading={loading}
        onLoad={() => setIsLoaded(true)}
        onError={handleError}
      />
    </div>
  );
};

/** Fallback (no SW) — fetch() with Bearer token + blob URL. */
const FetchImage: React.FC<AuthenticatedImageProps> = ({
  shopId, slideId, imageId, alt, className, style, loading,
  size, onHeicPending,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isHeicPending, setIsHeicPending] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const onHeicPendingRef = useRef(onHeicPending);
  onHeicPendingRef.current = onHeicPending;

  useEffect(() => {
    let isMounted = true;
    let currentBlobUrl: string | null = null;

    const fetchImage = async () => {
      try {
        setHasError(false);
        setIsHeicPending(false);
        setIsLoading(true);

        const response = await slideApiService.fetchAuthenticatedImage(shopId, slideId, imageId, size);
        if (!isMounted) return;

        if (response.ok) {
          const contentType = response.headers.get('Content-Type') || '';
          const isHeic = contentType.includes('image/heic') || contentType.includes('image/heif');
          if (isHeic) {
            setIsHeicPending(true);
            setIsLoading(false);
            onHeicPendingRef.current?.();
            return;
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
          currentBlobUrl = blobUrl;
          setImageSrc(blobUrl);
          setIsHeicPending(false);
          setIsLoading(false);
        } else {
          console.warn(`${SW_TAG} FetchImage: HTTP ${response.status} for imageId=${imageId}`);
          setHasError(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error(`${SW_TAG} FetchImage: fetch failed for imageId=${imageId}`, error);
        if (isMounted) { setHasError(true); setIsLoading(false); }
      }
    };

    fetchImage();
    return () => {
      isMounted = false;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [shopId, slideId, imageId, size, retryCount]);

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRetryCount(prev => prev + 1);
  };

  return (
    <div className={className} style={{ position: 'relative', ...style, overflow: 'hidden' }}>
      {isHeicPending && <HeicProcessingSpinner />}
      {isLoading && !isHeicPending && (
        <div
          className="flex items-center justify-center bg-gray-100 text-gray-500"
          style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        >
          <div className="text-center">
            <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mb-1"></div>
            <div className="text-xs">Loading...</div>
          </div>
        </div>
      )}
      {(hasError || (!isLoading && !imageSrc && !isHeicPending)) && (
        <div
          className="flex items-center justify-center bg-gray-100"
          style={{ position: 'absolute', inset: 0, zIndex: 1, cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={handleRetry}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px', color: 'var(--text-secondary)' }}>
              &#8635;
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Tap to retry</div>
          </div>
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: style?.objectFit || 'cover',
          }}
          loading={loading}
        />
      )}
    </div>
  );
};

/** Shared HEIC processing spinner shown while Cloud Function converts HEIC to PNG. */
const HeicProcessingSpinner: React.FC = () => (
  <div
    className="flex items-center justify-center"
    style={{ position: 'absolute', inset: 0, zIndex: 1, backgroundColor: 'var(--bg-secondary)' }}
  >
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'inline-block', width: '24px', height: '24px', marginBottom: '8px' }}>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: '100%', animation: 'spin 1s linear infinite' }}>
          <circle cx="12" cy="12" r="10" stroke="var(--primary-light)" strokeWidth="3" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--text-accent)" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-accent)' }}>Processing image</div>
    </div>
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default AuthenticatedImage;
